import { styled } from '@mui/system'
import { fontSizes, colors } from '../consts'
import { Tabs } from '../LazyComponents'

type TabHeaderStyleProps = {
  tabColor: 'light' | 'dark' | 'gray'
  indicolor?: string
  topRadius?: number
  bottomRadius?: number
  noBackground?: boolean
  fullwidth?: boolean
  tabItemPadding?: number[]
  tabItemTextTransform?: string
  minTabWidth?: number
  selectedColor?: string
  scrollButtonColor?: string
}

const shouldForwardProp = (prop: string) =>
  ![
    'tabItemPadding',
    'tabItemTextTransform',
    'tabColor',
    'indicolor',
    'topRadius',
    'bottomRadius',
    'noBackground',
    'fullwidth',
    'minTabWidth',
    'uiTheme',
    'selectedColor',
    'scrollButtonColor',
  ].includes(prop)

export const TabHeaderStyle = styled(Tabs, { shouldForwardProp })<TabHeaderStyleProps>(
  ({
    tabColor,
    indicolor,
    topRadius,
    bottomRadius,
    noBackground,
    fullwidth,
    minTabWidth,
    tabItemPadding,
    tabItemTextTransform,
    selectedColor,
    scrollButtonColor,
    theme,
  }) => ({
    width: fullwidth ? '100%' : 'auto',
    background: noBackground ? 'none' : getTabColor(tabColor, 'background'),
    borderRadius: `${topRadius ? `${topRadius}px ${topRadius}px` : `8px 8px`} ${
      bottomRadius ? `${bottomRadius}px ${bottomRadius}px` : `0 0`
    }`,
    '&& .MuiTabs-indicator': {
      background: indicolor ? indicolor : getTabColor(tabColor, 'indicator'),
    },
    '&& .MuiTab-root': {
      ...(tabItemPadding && {
        padding: tabItemPadding.map((pad) => theme.spacing(pad)).join(' '),
      }),
      ...(minTabWidth && {
        minWidth: minTabWidth,
        [theme.breakpoints.down('md')]: {
          minWidth: minTabWidth * 0.8,
        },
      }),
    },
    '&& .MuiTab-textColorPrimary': {
      color: getTabColor(tabColor, 'textColorPrimary'),
      fontWeight: 500,
      [theme.breakpoints.down('md')]: {
        fontSize: fontSizes.xSmall,
      },
      ...(tabItemTextTransform && { textTransform: tabItemTextTransform }),
    },
    '&& .MuiTab-textColorPrimary.Mui-selected': {
      color: selectedColor ? selectedColor : getTabColor(tabColor, 'Mui-selected'),
      fontWeight: 'bold',
      [theme.breakpoints.down('sm')]: {
        fontSize: fontSizes.xSmall,
      },
    },
    '&& .MuiTab-textColorInherit': {
      color: getTabColor(tabColor, 'textColorInherit'),
      fontWeight: 500,
      ...(tabItemTextTransform && { textTransform: tabItemTextTransform }),
    },
    '&& .MuiTabs-scrollButtons': {
      ...(scrollButtonColor && {
        color: scrollButtonColor,
      }),
    },
  })
)

export const LinkTab = styled(Tabs, { shouldForwardProp })<TabHeaderStyleProps>(
  ({ tabColor, tabItemPadding, tabItemTextTransform, minTabWidth, theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: theme.spacing(1, 2, 2),
    '&& .MuiTab-root': {
      ...(tabItemPadding && {
        padding: tabItemPadding.map((pad) => theme.spacing(pad)).join(' '),
      }),
      ...(minTabWidth && {
        minWidth: minTabWidth,
      }),
    },
    '&& .MuiTab-root a': {
      ...(tabItemTextTransform && { textTransform: tabItemTextTransform }),
    },
    '& .lnk': {
      color: getTabColor(tabColor, 'textColorPrimary'),
      fontWeight: 300,
      fontSize: '0.9rem',
      textDecoration: 'none',
      textTransform: 'uppercase',
      ...(tabItemTextTransform && { textTransform: tabItemTextTransform }),
      [theme.breakpoints.down('sm')]: {
        fontSize: fontSizes.small,
      },
      '& a': {
        color: getTabColor(tabColor, 'textColorPrimary'),
        fontWeight: 300,
        fontSize: '0.9rem',
        textDecoration: 'none',
        textTransform: 'uppercase',
        ...(tabItemTextTransform && { textTransform: tabItemTextTransform }),
        [theme.breakpoints.down('sm')]: {
          fontSize: fontSizes.small,
        },
      },
    },
  })
)

const getTabColor = (tabColor: 'dark' | 'light' | 'gray' = 'light', item: string) => {
  switch (item) {
    case 'background':
      return tabColor === 'dark'
        ? colors.black
        : tabColor === 'gray'
        ? colors.gray.gray20
        : colors.lightestGrey
    case 'indicator':
      return tabColor === 'dark'
        ? colors.yellow
        : tabColor === 'gray'
        ? colors.text
        : colors.black
    case 'textColorPrimary':
      return tabColor === 'dark'
        ? colors.gray.gray40
        : tabColor === 'gray'
        ? colors.gray.gray60
        : colors.black
    case 'Mui-selected':
      return tabColor === 'dark'
        ? colors.yellow
        : tabColor === 'gray'
        ? colors.text
        : colors.black
    case 'textColorInherit':
      return tabColor === 'dark' ? colors.yellow : colors.text
    default:
      return colors.gray.gray60
  }
}

type CircularTabsProps = {
  backgroundColor?: string
  uiTheme?: 'dark' | 'light'
  margins?: number[]
  tabItemPadding?: number[]
  tabItemTextTransform?: string
}

export const RoundedTabs = styled(Tabs, { shouldForwardProp })<CircularTabsProps>(
  ({
    theme,
    uiTheme,
    backgroundColor,
    margins,
    tabItemPadding,
    tabItemTextTransform,
  }) => ({
    width: '30%',
    background:
      backgroundColor || (uiTheme === 'dark' ? colors.gray.gray80 : colors.offWhite),
    borderRadius: 30,
    zIndex: 20,
    margin: theme.spacing(2, 0),
    ...(margins && {
      margin: margins.map((margin) => theme.spacing(margin)).join(' '),
    }),
    '&& .MuiTabs-indicator': {
      background: 'none',
    },
    '&& .MuiTab-root': {
      ...(tabItemPadding && {
        padding: tabItemPadding.map((pad) => theme.spacing(pad)).join(' '),
      }),
    },
    '&& .MuiTab-textColorPrimary': {
      color: uiTheme === 'dark' ? colors.white : colors.gray.gray60,
      fontWeight: 700,
      [theme.breakpoints.down('md')]: {
        fontSize: fontSizes.xSmall,
      },
      ...(tabItemTextTransform && { textTransform: tabItemTextTransform }),
    },
    '&& .Mui-selected': {
      color: uiTheme === 'dark' ? colors.white : colors.black,
      fontWeight: 'bold',
      [theme.breakpoints.down('md')]: {
        fontSize: fontSizes.xSmall,
      },
    },
    [theme.breakpoints.down('md')]: {
      width: '90%',
    },
  })
)

export const StyledTabPanel = styled('div')<{
  value: number
  index: number
  panelMargins?: number[]
  minHeight?: number | string
  border?: boolean
  borderColor?: string
}>(({ theme, value, index, panelMargins, minHeight, border, borderColor }) => ({
  transition: 'opacity 0.3s ease-out',
  opacity: value === index ? 1 : 0,
  height: '100%',
  overflow: 'hidden',
  ...(panelMargins && {
    margin: panelMargins.map((margin) => theme.spacing(margin)).join(' '),
  }),
  ...(minHeight && {
    minHeight: minHeight,
  }),
  ...(border && {
    border: `1px solid ${borderColor ?? colors.gray.gray20}`,
    borderTop: 'none',
    borderRadius: '0 0 12px 12px',
  }),
}))
